// src/vatandas/services/vatandasApi.js

import axios from "axios";

// Backend URL
const RAW_API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001/api";
const API_URL = String(RAW_API_URL).replace(/\/+$/, "");

if (!API_URL) {
  throw new Error("REACT_APP_BACKEND_URL bulunamadı ve fallback de üretilemedi.");
}

// LocalStorage keys (mevcut yapıyı bozmuyoruz)
const LS = {
  accessToken: "accessToken",
  refreshToken: "refreshToken",
  user: "user",
};

const getAccessToken = () => localStorage.getItem(LS.accessToken);
const getRefreshToken = () => localStorage.getItem(LS.refreshToken);
const setAccessToken = (t) => localStorage.setItem(LS.accessToken, t);

const clearSession = () => {
  // ❗ localStorage.clear() YAPMIYORUZ (belediye tarafını da bozuyor)
  localStorage.removeItem(LS.accessToken);
  localStorage.removeItem(LS.refreshToken);
  localStorage.removeItem(LS.user);
};

// Axios instance
const vatandasApi = axios.create({
  baseURL: API_URL,
  timeout: 10000,
});

// Header set helper (Axios v1 uyumlu)
const setHeader = (config, key, value) => {
  config.headers = config.headers || {};
  // AxiosHeaders olabilir
  if (typeof config.headers.set === "function") {
    config.headers.set(key, value);
  } else {
    config.headers[key] = value;
  }
};

// Auth endpoint guard: login/refresh 401 normaldir -> redirect/refresh akışına sokma
const isAuthEndpoint = (url = "") => {
  const u = String(url || "");
  // baseURL'li / baseURL'siz / trailing slash toleranslı
  return (
    u.includes("/token/refresh") ||
    u.includes("/token/") || // /token/ ve olası varyasyonlar
    u.endsWith("/token") ||
    u.endsWith("/token/refresh")
  );
};

// ---------------------------------------------
// Request Interceptor: Authorization + Content-Type
// ---------------------------------------------
vatandasApi.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      setHeader(config, "Authorization", `Bearer ${token}`);
    }

    // FormData ise Content-Type elle set ETME (boundary bozulmasın)
    const isFormData = typeof FormData !== "undefined" && config.data instanceof FormData;

    // JSON payload ise sadece gerektiğinde set et
    if (config.data && !isFormData) {
      // bazı durumlarda config.headers zaten set edilmiş olabilir
      const hasContentType =
        (typeof config.headers?.get === "function" && config.headers.get("Content-Type")) ||
        config.headers?.["Content-Type"] ||
        config.headers?.["content-type"];

      if (!hasContentType) {
        setHeader(config, "Content-Type", "application/json");
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ---------------------------------------------
// Response Interceptor: 401 -> refresh -> retry
// ---------------------------------------------
let isRefreshing = false;
let refreshQueue = [];

const processRefreshQueue = (error, newToken = null) => {
  refreshQueue.forEach((p) => {
    if (error) p.reject(error);
    else p.resolve(newToken);
  });
  refreshQueue = [];
};

vatandasApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    // Network error / CORS / timeout vs.
    if (!error?.response) return Promise.reject(error);

    const status = error.response.status;

    // Login/refresh endpointlerinde 401 -> burada hiçbir şey yapma, sadece reject et.
    // (LoginPage catch bloğu setError ile uyarı gösterecek.)
    if (status === 401 && originalRequest && isAuthEndpoint(originalRequest.url)) {
      return Promise.reject(error);
    }

    // 401 ve daha önce retry edilmemişse refresh dene
    if (status === 401 && originalRequest && !originalRequest._retry) {
      const refresh = getRefreshToken();

      // Refresh yoksa: direkt logout/redirect (auth endpointleri hariç)
      if (!refresh) {
        clearSession();
        window.location.href = "/giris";
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // aynı anda çok istek 401 alırsa tek refresh
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          refreshQueue.push({ resolve, reject });
        })
          .then((newToken) => {
            setHeader(originalRequest, "Authorization", `Bearer ${newToken}`);
            return vatandasApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      isRefreshing = true;

      try {
        // Refresh endpoint (DRF SimpleJWT standard)
        const { data } = await axios.post(`${API_URL}/token/refresh/`, { refresh });
        const newAccess = data?.access;

        if (!newAccess) {
          throw new Error("Refresh response access token içermiyor.");
        }

        setAccessToken(newAccess);
        processRefreshQueue(null, newAccess);

        setHeader(originalRequest, "Authorization", `Bearer ${newAccess}`);
        return vatandasApi(originalRequest);
      } catch (refreshErr) {
        processRefreshQueue(refreshErr, null);
        clearSession();
        window.location.href = "/giris";
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ---------------------------------------------
// API helpers
// ---------------------------------------------

export const fetchBm4ImportantPlaces = () => vatandasApi.get("/bm4/important-places/");

// ---------------------------------------------
// Citizen application create + OTP helpers
// ---------------------------------------------

/**
 * Vatandaş başvuru ekranı için OTP toggle/config bilgisi.
 * Backend action:
 * GET /api/core/applications/public-config/
 */
export const fetchPublicOtpConfigAPI = () =>
  vatandasApi.get("/core/applications/public-config/");

/**
 * OTP açıkken ilk adım:
 * Form verisini gönderir, gerçek başvuru oluşturmaz.
 * Session açar ve SMS doğrulama kodu gönderir.
 *
 * Not:
 * - FormData ile çalışır
 * - Toggle kapalıysa backend mevcut create akışına düşebilir
 */
export const preSubmitOtpAPI = (applicationData) =>
  vatandasApi.post("/core/applications/pre-submit-otp/", applicationData);

/**
 * Aynı session için yeni OTP kodu ister.
 * Body: { session_token }
 */
export const resendOtpAPI = (sessionToken) =>
  vatandasApi.post("/core/applications/resend-otp/", {
    session_token: sessionToken,
  });

/**
 * OTP kodunu doğrular ve başvuruyu finalize eder.
 * Body: { session_token, code }
 */
export const submitVerifiedAPI = (sessionToken, code) =>
  vatandasApi.post("/core/applications/submit-verified/", {
    session_token: sessionToken,
    code,
  });

// ---------------------------------------------
// Tracking helpers
// ---------------------------------------------

export const trackApplicationAPI = (phoneNumber, trackingNumber) =>
  vatandasApi.post("/core/applications/track/", {
    phone_number: phoneNumber,
    tracking_number: trackingNumber,
  });

export const trackApplicationByLinkAPI = (token) =>
  vatandasApi.get("/core/applications/track-by-link/", {
    params: { t: token },
  });

export const fetchStatusLogsAPI = ({ trackingNumber, phoneNumber, applicationId } = {}) =>
  vatandasApi.get("/staff/status-logs/", {
    params: applicationId
      ? { application: applicationId }
      : {
          tracking_number: trackingNumber,
          phone_number: phoneNumber,
        },
  });

export const fetchPublicApplicationSmsHistoryAPI = ({
  token,
  trackingNumber,
  phoneNumber,
} = {}) =>
  vatandasApi.get("/notifications/notifications/public-application-sms/", {
    params: token
      ? { t: token }
      : {
          tracking_number: trackingNumber,
          phone_number: phoneNumber,
        },
  });

export const fetchForwardLogsAPI = (applicationId) =>
  vatandasApi.get("/staff/forward-logs/", {
    params: { application: applicationId },
  });

// Vatandaşa teslim edilen belgeler (public; token VEYA takip no + telefon).
export const fetchSharedDocumentsAPI = ({ token, trackingNumber, phoneNumber } = {}) =>
  vatandasApi.get("/citizen-delivery/applications/public-documents/", {
    params: token
      ? { t: token }
      : { tracking_number: trackingNumber, phone_number: phoneNumber },
  });

// ---------------------------------------------
// Surveys
// ---------------------------------------------
export const fetchSurveysAPI = () => vatandasApi.get("/surveys/surveys/");
export const sendSurveyResponseAPI = (payload) => vatandasApi.post("/surveys/responses/", payload);

export default vatandasApi;